/**
 * Bangladesh customs duty cascading calculation.
 *
 * Stack:
 *   CD  = AV × cd%
 *   RD  = AV × rd%
 *   SD  = (AV + CD + RD) × sd%
 *   VAT = (AV + CD + RD + SD) × vat%
 *   AIT = (AV + CD + RD + SD + VAT) × ait%   [or AV × ait% if aitOnAv]
 *   AT  = (AV + CD + RD + SD + VAT) × at%    [or AV × at% if atOnAv]
 *
 * Total Duty = CD + RD + SD + VAT + AIT + AT
 * Landed Cost = AV + Total Duty
 */

export interface DutyRates {
  cd: number;
  rd: number;
  sd: number;
  vat: number;
  ait: number;
  at: number;
  aitOnAv?: boolean;
  atOnAv?: boolean;
}

export interface DutyLine {
  key: "cd" | "rd" | "sd" | "vat" | "ait" | "at";
  ratePct: number;
  base: number;
  amount: number;
}

export interface DutyResult {
  assessableValue: number;
  lines: DutyLine[];
  totalDuty: number;
  landedCost: number;
  effectiveRatePct: number;
}

export function computeDuty(assessableValue: number, rates: DutyRates): DutyResult {
  const av = Math.max(0, Number.isFinite(assessableValue) ? assessableValue : 0);
  const cd = av * (rates.cd || 0) / 100;
  const rd = av * (rates.rd || 0) / 100;
  const sdBase = av + cd + rd;
  const sd = sdBase * (rates.sd || 0) / 100;
  const vatBase = av + cd + rd + sd;
  const vat = vatBase * (rates.vat || 0) / 100;
  const aitBase = rates.aitOnAv ? av : av + cd + rd + sd + vat;
  const ait = aitBase * (rates.ait || 0) / 100;
  const atBase = rates.atOnAv ? av : av + cd + rd + sd + vat;
  const at = atBase * (rates.at || 0) / 100;

  const lines: DutyLine[] = [
    { key: "cd", ratePct: rates.cd || 0, base: av, amount: cd },
    { key: "rd", ratePct: rates.rd || 0, base: av, amount: rd },
    { key: "sd", ratePct: rates.sd || 0, base: sdBase, amount: sd },
    { key: "vat", ratePct: rates.vat || 0, base: vatBase, amount: vat },
    { key: "ait", ratePct: rates.ait || 0, base: aitBase, amount: ait },
    { key: "at", ratePct: rates.at || 0, base: atBase, amount: at },
  ];

  const totalDuty = cd + rd + sd + vat + ait + at;
  return {
    assessableValue: av,
    lines,
    totalDuty,
    landedCost: av + totalDuty,
    effectiveRatePct: av > 0 ? (totalDuty / av) * 100 : 0,
  };
}

export function computeCif(input: {
  invoiceForeign: number;
  exchangeRate: number;
  freight: number;
  insurance: number;
}): number {
  const inv = Math.max(0, input.invoiceForeign || 0);
  const fx = Math.max(0, input.exchangeRate || 0);
  const fr = Math.max(0, input.freight || 0);
  const ins = Math.max(0, input.insurance || 0);
  return inv * fx + fr + ins;
}
